import { Sparkles } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { TextInput } from '@/components/atoms'
import { SelectInput } from '@/components/atoms/inputs/SelectInput'
import RichTextEditor from '@/components/ui/RichTextEditor'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { PRODUCT_STATUS_OPTIONS } from '@/constants/statuses'
import { ProductModel } from '@/types/model/Product'
import { useGenerateVariationKeywords } from '@/hooks/features/products/useGenerateVariationKeywords'
import { Spinner } from '@/components/ui/spinner'

interface VariantFormFieldsProps {
    index: number
}

export const VariantFormFields = ({ index }: VariantFormFieldsProps) => {
    const { control, setValue, getValues } = useFormContext<ProductModel.ProductFormData>()
    const { id: productId } = useParams<{ id: string }>()
    const { isGenerating, generateKeywords } = useGenerateVariationKeywords()

    const variationId = getValues(`variations.${index}._id`)

    const handleGenerateKeywords = async () => {
        if (!productId || !variationId) return

        const keywords = await generateKeywords(productId, variationId)
        if (keywords?.length) {
            setValue(`variations.${index}.search_keywords`, keywords.join(', '), { shouldDirty: true })
        }
    }

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <TextInput
                    control={control}
                    name={`variations.${index}.name`}
                    label="Variant name"
                    placeholder="e.g. Blue - Large"
                />
                <SelectInput
                    control={control}
                    name={`variations.${index}.status`}
                    label="Status"
                    placeholder="Select status"
                    options={PRODUCT_STATUS_OPTIONS}
                />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextInput
                    control={control}
                    name={`variations.${index}.regular_price`}
                    label="Price"
                    type="number"
                    valueAsNumber
                />
                <TextInput
                    control={control}
                    name={`variations.${index}.purchase_cost`}
                    label="Cost per item"
                    type="number"
                    valueAsNumber
                />
            </div>

            <div className="space-y-4">
                <TextInput
                    control={control}
                    name={`variations.${index}.summary`}
                    label="Summary"
                    placeholder="Brief summary for this variant"
                />

                <TextInput
                    control={control}
                    name={`variations.${index}.search_keywords`}
                    label="Search keywords"
                    placeholder="Comma-separated alternate search phrases, e.g. smd 5050, smd-5050"
                    multiline
                    rows={2}
                    disabled={isGenerating}
                    rules={{
                        required: 'Search keywords are required',
                        minLength: {
                            value: 20,
                            message: 'Search keywords must be at least 20 characters',
                        },
                    }}
                    rightElement={
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleGenerateKeywords}
                            disabled={isGenerating || !variationId}
                            title={!variationId ? 'Save the variation first to auto-generate keywords' : 'Auto-generate keywords'}>
                            {isGenerating ? <Spinner /> : <Sparkles />}
                        </Button>
                    }
                />

                <Accordion type="multiple" className="w-full space-y-2">
                    <AccordionItem value="description" className="rounded-lg border border-border px-3">
                        <AccordionTrigger className="py-2.5 hover:no-underline">
                            <span className="text-sm font-medium text-ink">Full description</span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-3 pt-1">
                            <Controller
                                control={control}
                                name={`variations.${index}.description`}
                                render={({ field, fieldState: { error } }) => (
                                    <div className="space-y-1.5">
                                        <RichTextEditor
                                            value={field.value}
                                            onChange={field.onChange}
                                            className="min-h-[180px]"
                                            placeholder="Provide a detailed description of this specific variation"
                                        />
                                        {error && <p className="text-sm text-error">{error.message}</p>}
                                    </div>
                                )}
                            />
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="delivery" className="rounded-lg border border-border px-3">
                        <AccordionTrigger className="py-2.5 hover:no-underline">
                            <span className="text-sm font-medium text-ink">Delivery and return policy</span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-3 pt-1">
                            <Controller
                                control={control}
                                name={`variations.${index}.delivery_return_policy`}
                                render={({ field, fieldState: { error } }) => (
                                    <div className="space-y-1.5">
                                        <RichTextEditor
                                            value={field.value}
                                            onChange={field.onChange}
                                            className="min-h-[180px]"
                                            placeholder="Variation specific delivery and return terms"
                                        />
                                        {error && <p className="text-sm text-error">{error.message}</p>}
                                    </div>
                                )}
                            />
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>
        </div>
    )
}
